// filepath: apps/web/src/admin/locations/api.ts
//
// ISS-515 — typed REST client for /api/directory/locations/*.
//
// Mirrors the wire shape at internal/directory/location/handlers.go
// (RegisterRoutes, handlers.go:62-79). The Go handler exposes 6
// routes; the SPA package wraps each one with typed inputs/outputs,
// the canonical `apiFetch` client (bearer header + 401 silent-
// refresh retry chain), and INV-8 idempotency for every mutation.
//
// INV-2: server is the single source of truth on what is
// visible. The Go handler at handlers.go:140,164 uses
// `authorization.WriteStripped(..., "location", out)` -- the SPA
// MUST NOT synthesise display fields the server did not emit
// (FR-AUTH-6 / `entity.Authorizer.StripFields`).
//
// INV-7: every mutation carries `row_version` (OCC). The
// row_version the client sends is the one echoed back by the
// last GET response; the server rejects with 409 on mismatch.
//
// INV-8: every mutating call carries `X-Idempotency-Key`. The
// helper `newIdempotencyKey()` is the canonical mint (the same
// helper used by features/records/comments/api.ts and
// admin/counterparties/api.ts).
//
// INV-9: every value in error envelopes is rendered as JSX text;
// the SPA must never `dangerouslySetInnerHTML` a server-sent
// string.

import { apiFetch } from "@/api";
import { ApiError } from "@/definitions/types";
import type { Location } from "@/features/directory/types";

/**
 * Wire shape of a Location record as returned by the backend.
 * Alias of `features/directory/types.ts#Location` so the admin
 * module and the features module agree without a circular
 * re-export (the admin form imports the API; the API imports
 * the features module's Location).
 */
export type { Location };

/**
 * Input for `createLocation` (no id / row_version / audit fields).
 */
export interface CreateLocationInput {
  /** Optional parent in the hierarchical location layout. null =
   * root. The form's parent picker prevents self-parent on edit
   * (the server enforces cycle-safe reparent separately via the
   * `/move` endpoint). */
  parent_location_id?: string | null;
  name: string;
  code?: string | null;
}

/**
 * Input for `updateLocation` -- INV-7 row_version is mandatory.
 */
export interface UpdateLocationInput {
  parent_location_id?: string | null;
  name?: string;
  code?: string | null;
  row_version: number;
}

/** Optional parameters for `listLocations`. */
export interface ListLocationsOpts {
  /** Filter to direct children of this parent. */
  parent_location_id?: string;
  /** When true, include status='closed' locations. Default false. */
  include_closed?: boolean;
  /** Page size (Go caps at 200, default 50). */
  limit?: number;
  /** Page offset (Go: default 0). */
  offset?: number;
}

/** Wire envelope shape returned by `GET /api/directory/locations`. */
export interface LocationsListEnvelope {
  records: Location[];
  total: number;
}

/**
 * Generate a v4 UUID via crypto.randomUUID() with a Date.now
 * fallback. Mirror of admin/counterparties/api.ts#newIdempotencyKey.
 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * callApi -- thin wrapper around `apiFetch` that throws
 * `ApiError(message, status, code)` on non-2xx responses,
 * handles the 204 No Content body, and forwards an explicit
 * `X-Idempotency-Key` header (INV-8).
 *
 * Loosely modeled on `admin/counterparties/api.ts#callApi` --
 * same wire contract (status + code + message envelope).
 */
async function callApi<T>(
  url: string,
  opts: {
    method?: "GET" | "POST" | "PATCH" | "DELETE";
    body?: unknown;
    idempotencyKey?: string;
  } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
  };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
  }
  const resp = await apiFetch(url, {
    method: opts.method ?? "GET",
    init,
    idempotencyKey: opts.idempotencyKey,
  });
  if (!resp.ok) {
    let code = "";
    let message = `HTTP ${resp.status}`;
    try {
      const errBody = (await resp.json()) as {
        code?: string;
        message?: string;
        error?: string;
      };
      if (typeof errBody.code === "string") code = errBody.code;
      if (typeof errBody.message === "string") message = errBody.message;
      else if (typeof errBody.error === "string") message = errBody.error;
    } catch {
      /* not JSON, fall back to status string */
    }
    throw new ApiError(message, resp.status, code);
  }
  if (resp.status === 204) {
    return undefined as unknown as T;
  }
  return (await resp.json()) as T;
}

const BASE = "/api/directory/locations";

/**
 * POST /api/directory/locations -- 201 + the created row.
 */
export async function createLocation(
  input: CreateLocationInput,
  idempotencyKey: string,
): Promise<Location> {
  // Drop undefined fields so the server's `omitempty` JSON
  // decoders handle them correctly (the Go struct at
  // handlers.go:83-87 takes ParentLocationID *uuid.UUID +
  // Code *string -- both tolerate null/absent).
  const body: Record<string, unknown> = { name: input.name };
  if (input.parent_location_id) {
    body.parent_location_id = input.parent_location_id;
  }
  if (input.code) {
    body.code = input.code;
  }
  return callApi<Location>(BASE, {
    method: "POST",
    body,
    idempotencyKey,
  });
}

/**
 * GET /api/directory/locations?... -- `{records, total}` envelope
 * (per Go's WriteStrippedList convention; mirror of
 * admin/counterparties/api.ts#listCounterparties).
 */
export async function listLocations(
  opts?: ListLocationsOpts,
): Promise<Location[]> {
  const params = new URLSearchParams();
  if (opts?.parent_location_id)
    params.set("parent_location_id", opts.parent_location_id);
  if (opts?.include_closed) params.set("include_closed", "true");
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts?.offset !== undefined) params.set("offset", String(opts.offset));
  const qs = params.toString();
  const url = qs ? `${BASE}?${qs}` : BASE;
  const body = (await callApi<unknown>(url)) as LocationsListEnvelope;
  // Defensive: if the wire envelope ever drifts, return [] rather
  // than crashing the list page (mirrors the ISS-482 pattern in
  // admin/counterparties/api.ts#listCounterparties).
  if (!body || !Array.isArray(body.records)) return [];
  return body.records;
}

/**
 * GET /api/directory/locations/{id}?id={uuid}
 * The Go handler reads the canonical id from the `?id=` query
 * param (handlers.go:144-149), so the SPA MUST send both the
 * path-segment `{id}` AND the query param. The path-segment id
 * is matched by Go 1.22+ mux wildcards; the canonical id is
 * taken from the query string.
 */
export async function getLocation(id: string): Promise<Location> {
  return callApi<Location>(
    `${BASE}/${encodeURIComponent(id)}?id=${encodeURIComponent(id)}`,
  );
}

/**
 * PATCH /api/directory/locations/{id}?id={uuid} -- 204 No Content.
 * Caller MUST supply `row_version` (INV-7). The server returns 409
 * with `code='row_version_mismatch'` on stale submission.
 */
export async function updateLocation(
  id: string,
  input: UpdateLocationInput,
  idempotencyKey: string,
): Promise<void> {
  return callApi<void>(
    `${BASE}/${encodeURIComponent(id)}?id=${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      body: {
        row_version: input.row_version,
        fields: {
          parent_location_id: input.parent_location_id ?? null,
          name: input.name,
          code: input.code ?? null,
        },
      },
      idempotencyKey,
    },
  );
}

/**
 * POST /api/directory/locations/{id}/move?id={uuid}
 *
 * Cycle-safe reparent. The Go handler at
 * handlers.go:200-226 reads the new parent from the request body
 * and rejects with 422 + ErrCycleDetected if the move would
 * create a cycle (handlers.go:306).
 */
export async function moveLocation(
  id: string,
  expectedRowVersion: number,
  newParentId: string | null,
  idempotencyKey: string,
): Promise<void> {
  return callApi<void>(
    `${BASE}/${encodeURIComponent(id)}/move?id=${encodeURIComponent(id)}`,
    {
      method: "POST",
      body: {
        row_version: expectedRowVersion,
        new_parent_id: newParentId,
      },
      idempotencyKey,
    },
  );
}

/**
 * POST /api/directory/locations/{id}/close?id={uuid}
 *
 * Archive (the SPA-facing verb is "Archive"; the Go verb is
 * "close" but the BA spec for ISS-515 names the UI verb "Archive").
 * Returns 204 No Content. Idempotent at the server level --
 * a second close returns 422 (`ErrInvalidInput`) and does NOT
 * change the row.
 */
export async function archiveLocation(
  id: string,
  expectedRowVersion: number,
  idempotencyKey: string,
): Promise<void> {
  return callApi<void>(
    `${BASE}/${encodeURIComponent(id)}/close?id=${encodeURIComponent(id)}`,
    {
      method: "POST",
      body: { row_version: expectedRowVersion },
      idempotencyKey,
    },
  );
}
